import type { PageKey } from "../content/meta";
import type { Sport } from "../services/api/schemas";

/**
 * WHICH SPORTS HAVE A PER-FIXTURE PAGE, AND WHERE EVERY PART OF IT LIVES.
 *
 * ── THE BUG THIS EXISTS TO RETIRE ───────────────────────────────────────────
 * `FixtureListSection.hrefFor` used to read, in full:
 *
 *     if (f.sport === "football") return `/app/football/fixture/${…}`;
 *     return null;
 *
 * The NFL exporter had been building sixteen fixture pages a day and uploading
 * them for a week; nothing on the landing page or `/fixtures` linked to one,
 * because that `null` is what an NFL row got. Nothing failed — a missing link
 * is not an error, it is a row that renders as plain text — and it was found
 * by Ned trying to click one.
 *
 * A SECOND HARDCODED BRANCH WOULD BE THE SAME BUG WITH A LONGER BODY. The fix
 * is not "remember to add sports to the list"; it is to leave only ONE list, so
 * that adding a sport is one row and a half-finished addition is impossible to
 * express. `boards.ts` states the rule for boards ("ONE list, because the last
 * arrangement lost a whole feature" — a built, deployed football prop board
 * that was linked from nowhere) and `routes.ts` states it for the sitemap; this
 * is the same rule for fixture pages.
 *
 * ── WHAT MAKES IT NOT DRIFT ─────────────────────────────────────────────────
 * Five things read this table and there is no second place any of them could
 * get the answer:
 *
 *   1. `hrefFor` (FixtureListSection) — whether a row is a link at all.
 *   2. `App.tsx` — the routes themselves, REGISTERED BY MAPPING OVER THIS
 *      ARRAY. A sport cannot have a link with no route, or a route no list
 *      links to, because both come from the same row.
 *   3. `useFixturePage` — the gated `GET {gatedPath}/{key}` call.
 *   4. `useFreeFixtures` — which free artifact the account tier reads.
 *   5. `upNav` — the "up" target from a fixture page of any sport.
 *
 * So this file answers "does a page exist for this row?" with a FACT ABOUT THE
 * BACKEND — is there a published document and a route that serves it — rather
 * than with a sport name someone remembered to type. A sport whose exporter is
 * written but whose document is not published yet is simply not in this array,
 * and its rows stay plain text, honestly.
 *
 * ⚠️ WHY NOT DERIVE IT FROM THE PAYLOAD INSTEAD. The obvious alternative is a
 * `hasPage` flag on each public fixture row, so the server decides. It was
 * rejected: the SPA route and the API path are client facts the server has no
 * business asserting, the public file is shared and written by three exporters
 * that would each have to agree on the flag, and a row that claimed a page the
 * client has no route for would render a link to a 404. The client knows what
 * it can render; the table says so once.
 */
export interface FixturePageSource {
  sport: Sport;
  /** The SPA route, WITHOUT the trailing `/:key`. */
  path: string;
  /** The gated API route, without the trailing `/{key}` — `handler_season.py`
      picks the sport's document off this route's own template. */
  gatedPath: string;
  /** The `?artifact=` selector on `/teaser` for this sport's FREE file. Two
      objects written by two exporters, so two selectors — see
      `handler_teaser.py`'s note on why this is not `?sport=` on one. */
  freeArtifact: string;
  /** Ties the page to its title/description so the two cannot drift. */
  meta: PageKey;
}

export const FIXTURE_PAGES: readonly FixturePageSource[] = [
  {
    sport: "football",
    path: "/app/football/fixture",
    gatedPath: "/football/fixture",
    freeArtifact: "fixtures-free",
    meta: "footballFixturePage",
  },
  {
    /* ADDED 2026-09-08. The document has been built since the exporter landed;
       what was missing was every one of the five readers above. Its own `meta`
       rather than football's, because the NFL page genuinely shows less (no
       market comparison, no movement, no props) and a description promising
       those would be the page overclaiming in a search result. */
    sport: "nfl",
    path: "/app/nfl/fixture",
    gatedPath: "/nfl/fixture",
    freeArtifact: "nfl-fixtures-free",
    meta: "nflFixturePage",
  },
];

/** The fixture-page source for a sport, or `undefined` when no page exists for
    it. `undefined` is the honest answer and every caller renders it as an
    absence — never as a link to a route that does not exist. */
export function fixturePageFor(sport: string): FixturePageSource | undefined {
  return FIXTURE_PAGES.find((p) => p.sport === sport);
}

/** The path to one fixture's page, or `null` when that sport has none. The key
    is percent-encoded here, once, so no caller has to remember to. */
export function fixturePagePath(sport: string, key: string): string | null {
  const source = fixturePageFor(sport);
  return source ? `${source.path}/${encodeURIComponent(key)}` : null;
}

/** Is `pathname` a fixture page of any sport? Used by `upNav` so a new sport's
    page gets the same "up" control as football's without a second branch. */
export function isFixturePagePath(pathname: string): boolean {
  return FIXTURE_PAGES.some((p) => pathname.startsWith(`${p.path}/`));
}
